import * as React from 'react'

import type { IconsModule } from './icons'

/**
 * Module-level cache of the *in-flight* import promise only, so concurrent
 * mounts share a single dynamic import rather than each triggering their own.
 *
 * It is reset to `null` as soon as the promise settles — on both fulfillment
 * and rejection. Caching a resolved value here would defeat the retry path: a
 * later mount (or a test forcing the chunk to fail) would read the stale
 * fulfilled promise and never re-attempt the import. The browser's module
 * registry already makes a repeat `import()` free, so dropping the cache on
 * settle costs nothing while keeping retries and failing tests honest.
 */
let iconsPromise: Promise<IconsModule> | null = null

type UseIconsResult = {
  /** Resolved glyph map once the lazy chunk has loaded; null while pending. */
  icons: IconsModule | null
  /** Set when the lazy icon chunk fails to load, surfacing a retry affordance. */
  error: boolean
  /** Clears the error and re-attempts loading the lazy icon chunk. */
  retry: () => void
}

/**
 * Lazily loads the code-split glyph chunk and exposes its load state.
 *
 * State starts empty and is only ever populated from the resolved import, never
 * hydrated synchronously from the cached promise — so the first render always
 * observes the pending (skeleton) state, which the picker's loading UI relies on.
 */
export function useIcons(): UseIconsResult {
  const [icons, setIcons] = React.useState<IconsModule | null>(null)
  const [error, setError] = React.useState(false)

  React.useEffect(() => {
    if (icons || error) return
    let cancelled = false
    iconsPromise ??= import('./icons').then((module) => module.ICONS)
    const promise = iconsPromise

    const settle = async (): Promise<void> => {
      try {
        const resolved = await promise
        if (!cancelled) setIcons(resolved)
      } catch {
        if (!cancelled) setError(true)
      } finally {
        // Drop the cache once settled so the next mount (or a retry) re-imports.
        if (iconsPromise === promise) iconsPromise = null
      }
    }
    void settle()

    return (): void => {
      cancelled = true
    }
  }, [icons, error])

  // Clearing the error re-arms the effect, which re-attempts the import.
  const retry = React.useCallback((): void => {
    setError(false)
  }, [])

  return { icons, error, retry }
}
